/**
 * BulkStockEditorDialog — per-SKU quantities for a multi-row receive.
 *
 * Opens from the floating selection bar (never from another dialog —
 * DESIGN.md §10 forbids modal-from-modal). One submit creates ONE adjustment
 * document (`bulk_import_batches`, `import_type='bulk_adjust'`) with an
 * optional operator reference on the header, so the whole batch shows up as a
 * single undoable row in Logs → Adjustments.
 *
 * Blank row = skipped. Selecting 10 rows and filling 8 is a legal, common
 * shape (the operator selects a colourway block, then counts what actually
 * arrived), so the editor treats blanks as intent, not as an error.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { cn } from '@/lib/utils';
import type { BulkStockDirection } from '@threadheads/shared';
import { useLocation } from '@/contexts/LocationContext';
import { useInventory } from '../hooks/useInventory';
import { availableStock } from '../utils/stockUtils';
import { Product } from '../types';
import { AdjustmentReferenceField } from './AdjustmentReferenceField';
import ProductImageWithHover from '@/features/purchase-orders/components/ProductImageWithHover';

/**
 * DESIGN.md §14: tables past 200 rows must virtualize. Rather than
 * virtualizing a form (every row owns focus + input state, which virtualizing
 * would silently drop on scroll), the editor refuses the selection and asks
 * the operator to narrow it.
 */
const MAX_ROWS = 200;

const numberFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

interface BulkStockEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Selected rows, already narrowed by the active filters, in table order. */
  products: Product[];
  /** Called after a successful submit — clear selection + refetch. */
  onSuccess: () => void;
  /** Caller's org id; used only to decide whether a row needs owner context. */
  ownOrgId?: string;
  /** Resolved org names for cross-org rows at a shared 3PL location. */
  orgIdToName?: Map<string, string>;
}

export function BulkStockEditorDialog({
  open,
  onOpenChange,
  products,
  onSuccess,
  ownOrgId,
  orgIdToName,
}: BulkStockEditorDialogProps) {
  const { t } = useTranslation('inventory');
  const { selectedLocation } = useLocation();
  // Mirrors BulkActionModal: the dialog owns its own useInventory() instance
  // so the wiring stays consistent across the two bulk write surfaces.
  // BLU-1189: gated on `open`. The dialog stays mounted for the selection
  // bar's exit animation and its key carries no production-type filter, so an
  // ungated catalogue here is a second full fetch the moment the operator
  // filters the Inventory page. `bulkAddStockPerSku` resolves the filled rows
  // against that catalogue, so submit waits for it.
  const { bulkAddStockPerSku, loading: catalogueLoading } = useInventory(undefined, {
    enabled: open,
  });

  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [reference, setReference] = useState('');
  // Whole-sheet direction. One direction per adjustment document (separate
  // inbound/outbound docs) — quantities survive a toggle
  // because the counted units are the same either way.
  const [mode, setMode] = useState<BulkStockDirection>('add');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isConsume = mode === 'consume';
  // Applied to every "new total" computation so the sign arithmetic is
  // written exactly once for both directions; the per-row bits are hoisted
  // here so they aren't re-branched inside the 200-row map.
  const sign = isConsume ? -1 : 1;
  const newTotalClass = isConsume ? 'text-red-700' : 'text-emerald-700';
  const qtyAriaKey = isConsume
    ? 'bulkStockEditor.qtyAriaConsume'
    : 'bulkStockEditor.qtyAria';

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const submitRef = useRef<HTMLButtonElement>(null);

  // Fresh sheet every time. A stale quantity carried into a different
  // selection is the one mistake this surface must never make.
  useEffect(() => {
    if (!open) return;
    setQuantities({});
    setReference('');
    setMode('add');
    setSubmitError(null);
    setIsSubmitting(false);
  }, [open]);

  const tooManyRows = products.length > MAX_ROWS;

  const rows = useMemo(
    () =>
      products.map((product) => {
        const raw = (quantities[product.id] ?? '').trim();
        const parsed = raw === '' ? null : Number(raw);
        // Blank is intent (skip the row); anything that is not a whole number
        // of 1 or more is an error the operator must resolve before submit.
        const isInvalid = parsed !== null && (!Number.isInteger(parsed) || parsed < 1);
        const quantity = parsed !== null && !isInvalid ? parsed : null;
        const rowOwnerId = product.ownerOrganizationId;
        const ownerName =
          rowOwnerId && ownOrgId && rowOwnerId !== ownOrgId
            ? orgIdToName?.get(rowOwnerId)
            : undefined;

        return {
          id: product.id,
          sku: product.sku,
          imageUrl: product.image_url,
          available: availableStock(product),
          raw,
          quantity,
          isInvalid,
          ownerName,
        };
      }),
    [products, quantities, ownOrgId, orgIdToName],
  );

  // One pass over the rows per edit (they re-run on every keystroke):
  // submit payload, totals, and validity in a single memo.
  const { filledRows, hasInvalidRow, totalUnits, negativeRowCount } = useMemo(() => {
    const filled: Array<{ id: string; quantity: number }> = [];
    let invalid = false;
    let units = 0;
    let negative = 0;
    for (const row of rows) {
      if (row.isInvalid) invalid = true;
      if (row.quantity === null) continue;
      filled.push({ id: row.id, quantity: row.quantity });
      units += row.quantity;
      // Negative stock is allowed by design (discrepancies stay visible),
      // so a consume that overdraws warns rather than blocks.
      if (isConsume && row.available - row.quantity < 0) negative += 1;
    }
    return {
      filledRows: filled,
      hasInvalidRow: invalid,
      totalUnits: units,
      negativeRowCount: negative,
    };
  }, [rows, isConsume]);
  const skippedCount = rows.length - filledRows.length;

  const submitDisabled =
    tooManyRows || hasInvalidRow || filledRows.length === 0 || isSubmitting || catalogueLoading;
  // The spinner covers both waits the operator can be sitting through: the
  // catalogue arriving and the submit itself.
  const isBusy = isSubmitting || catalogueLoading;

  const handleSubmit = useCallback(async () => {
    if (submitDisabled) return;
    setIsSubmitting(true);
    setSubmitError(null);
    const ok = await bulkAddStockPerSku(filledRows, reference.trim() || undefined, mode);
    setIsSubmitting(false);
    if (ok) {
      // Success toast is owned by the mutation layer (it knows the resolved
      // totals + reference); a second toast here would double up.
      onSuccess();
    } else {
      setSubmitError(
        t(mode === 'consume' ? 'bulkStockEditor.submitFailedConsume' : 'bulkStockEditor.submitFailed'),
      );
    }
  }, [bulkAddStockPerSku, filledRows, mode, onSuccess, reference, submitDisabled, t]);

  const handleDialogKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      if (submitDisabled) return;
      event.preventDefault();
      void handleSubmit();
    }
  };

  // Warehouse muscle memory: Enter walks down the column, and the last row
  // hands focus to the submit button rather than dead-ending.
  const handleQuantityKeyDown = (
    event: ReactKeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (event.key !== 'Enter' || event.metaKey || event.ctrlKey) return;
    event.preventDefault();
    // Bound by the CURRENT row count — a shorter selection can leave stale
    // refs from a previous open behind.
    const next = index + 1 < rows.length ? inputRefs.current[index + 1] : null;
    if (next) {
      next.focus();
      next.select();
    } else {
      submitRef.current?.focus();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* One column wider than the 2xl default to pay for the thumbnail
          column without squeezing the SKU + owner cell. */}
      <DialogContent className="sm:max-w-3xl" onKeyDown={handleDialogKeyDown}>
        <DialogHeader>
          <DialogTitle>{t('bulkStockEditor.title')}</DialogTitle>
          <DialogDescription>
            {t(isConsume ? 'bulkStockEditor.descriptionConsume' : 'bulkStockEditor.description')}
          </DialogDescription>
        </DialogHeader>

        {tooManyRows ? (
          <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <div className="space-y-1">
              <p className="font-medium">{t('bulkStockEditor.tooManyTitle')}</p>
              <p>
                {t('bulkStockEditor.tooManyBody', {
                  count: products.length,
                  max: MAX_ROWS,
                })}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <SegmentedControl
              ariaLabel={t('bulkStockEditor.modeAria')}
              value={mode}
              onChange={setMode}
              size="md"
              options={[
                { value: 'add', label: t('bulkStockEditor.modeAdd') },
                { value: 'consume', label: t('bulkStockEditor.modeConsume') },
              ]}
            />
            <div className="grid gap-2">
              <AdjustmentReferenceField
                id="bulk-stock-editor-reference"
                value={reference}
                onChange={setReference}
              />
              <p className="text-xs text-muted-foreground">
                {selectedLocation
                  ? t('bulkStockEditor.hint', { location: selectedLocation.name })
                  : t('filters.allLocationsWarning')}
              </p>
            </div>

            {/* Neutralise shadcn Table's inner overflow wrapper so the sticky
                header resolves against THIS scroll box (same trick as
                InventoryTableList, DESIGN.md §14.2). */}
            <div className="max-h-[60vh] overflow-y-auto rounded-md border [&>div]:overflow-visible">
              <Table>
                <TableHeader className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:h-9 [&_th]:bg-background [&_th]:shadow-[inset_0_-1px_0_0_hsl(var(--border))]">
                  <TableRow>
                    <TableHead className="w-14 px-3">
                      {t('table.image')}
                    </TableHead>
                    <TableHead className="px-3">
                      {t('bulkStockEditor.columnSku')}
                    </TableHead>
                    <TableHead className="w-28 px-3 text-right">
                      {t('bulkStockEditor.columnCurrent')}
                    </TableHead>
                    <TableHead className="w-28 px-3 text-right">
                      {t(isConsume ? 'bulkStockEditor.columnRemove' : 'bulkStockEditor.columnAdd')}
                    </TableHead>
                    <TableHead className="w-28 px-3 text-right">
                      {t('bulkStockEditor.columnNewTotal')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, index) => {
                    const isSkipped = row.quantity === null && !row.isInvalid;
                    return (
                      <TableRow
                        key={row.id}
                        className={cn(isSkipped && 'text-muted-foreground')}
                      >
                        {/* Same thumbnail primitive as the inventory table —
                            hover pops the 200px preview, so the operator can
                            confirm they're counting the right garment without
                            leaving the sheet. */}
                        <TableCell className="px-3 py-1.5">
                          <ProductImageWithHover
                            imageUrl={row.imageUrl}
                            productSku={row.sku}
                            size="medium"
                          />
                        </TableCell>
                        <TableCell className="px-3 py-1.5">
                          <span className="font-mono text-sm">{row.sku}</span>
                          {row.ownerName && (
                            <span className="block text-[11px] text-muted-foreground">
                              {row.ownerName}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="px-3 py-1.5 text-right text-muted-foreground tabular-nums">
                          {numberFormatter.format(row.available)}
                        </TableCell>
                        <TableCell className="px-3 py-1.5 text-right">
                          <Input
                            ref={(element) => {
                              inputRefs.current[index] = element;
                            }}
                            type="number"
                            min={1}
                            step={1}
                            inputMode="numeric"
                            value={row.raw}
                            aria-label={t(qtyAriaKey, { sku: row.sku })}
                            aria-invalid={row.isInvalid || undefined}
                            className={cn(
                              'h-8 w-20 text-right tabular-nums',
                              row.isInvalid && 'border-destructive',
                            )}
                            onChange={(event) =>
                              setQuantities((prev) => ({
                                ...prev,
                                [row.id]: event.target.value,
                              }))
                            }
                            onKeyDown={(event) => handleQuantityKeyDown(event, index)}
                          />
                        </TableCell>
                        <TableCell className="px-3 py-1.5 text-right tabular-nums">
                          {row.quantity !== null ? (
                            <span className={cn('font-medium', newTotalClass)}>
                              {numberFormatter.format(row.available + sign * row.quantity)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">
                              {row.isInvalid ? '-' : t('bulkStockEditor.skipped')}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {hasInvalidRow && (
              <p className="text-sm text-destructive">
                {t('bulkStockEditor.invalidQty')}
              </p>
            )}
            {negativeRowCount > 0 && (
              <p className="flex items-start gap-1.5 text-sm text-amber-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                {t('bulkStockEditor.negativeWarning', { count: negativeRowCount })}
              </p>
            )}
            {submitError && (
              <p className="text-sm text-destructive" role="alert">
                {submitError}
              </p>
            )}
          </div>
        )}

        {/* `sm:mr-auto` on the summary does the splitting — overriding the
            footer's own `sm:justify-end` would rely on utility ordering. */}
        <DialogFooter className="sm:items-center">
          {/* role="status" is implicitly polite — screen readers get the
              running total without a second aria-live declaration. */}
          {!tooManyRows && (
            <p className="text-sm text-muted-foreground tabular-nums sm:mr-auto" role="status">
              {t('bulkStockEditor.summarySkus', {
                filled: filledRows.length,
                total: rows.length,
              })}
              <span aria-hidden="true"> · </span>
              <span className="font-medium text-foreground">
                {t(
                  isConsume ? 'bulkStockEditor.summaryUnitsConsume' : 'bulkStockEditor.summaryUnits',
                  { units: numberFormatter.format(totalUnits) },
                )}
              </span>
              {skippedCount > 0 && (
                <>
                  <span aria-hidden="true"> · </span>
                  {t('bulkStockEditor.summarySkipped', { count: skippedCount })}
                </>
              )}
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('addStockModal.cancel')}
            </Button>
            <Button
              ref={submitRef}
              onClick={handleSubmit}
              disabled={submitDisabled}
              className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
              aria-keyshortcuts="Meta+Enter"
              aria-busy={isBusy || undefined}
            >
              {isBusy && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {t(isConsume ? 'bulkStockEditor.submitConsume' : 'bulkStockEditor.submit')}
              {!isBusy && (
                <kbd className="ml-1 rounded border border-border/60 bg-white/10 px-1 font-mono text-[10px]">
                  ⌘↵
                </kbd>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
